import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Trends from './pages/Trends';
import Insights from './pages/Insights';
import OpenCyclesTrends from './pages/OpenCyclesTrends';
import ChatWindow from './components/ChatWindow'; // Enabled for development testing
import AuthPage from './components/AuthPage';

function App() {
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false); // Start expanded
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Initialize auth state - don't trust localStorage until validated
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const [authError, setAuthError] = useState<string>('');
  
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setIsChatCollapsed(false);
    }
  };

  const handleChatCollapse = (collapsed: boolean) => {
    setIsChatCollapsed(collapsed);
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Validate existing Access Key on app load
  useEffect(() => {
    const validateAuth = async () => {
      // Check for any stored auth error first (from previous redirect)
      const storedError = localStorage.getItem('salesai-auth-error');
      if (storedError) {
        setAuthError(storedError);
        localStorage.removeItem('salesai-auth-error');
        setIsCheckingAuth(false);
        return; // Don't proceed with validation if there's an error
      }

      // Check URL parameter first
      const urlParams = new URLSearchParams(window.location.search);
      const urlKey = urlParams.get('key');
      
      if (urlKey) {
        // Store the original path before cleaning the URL
        const originalPath = window.location.pathname;
        console.log('🔑 URL key found, original path:', originalPath);
        
        // Test the URL key
        const isValid = await testApiKey(urlKey);
        if (isValid) {
          localStorage.setItem('salesai-api-key', urlKey);
          setApiKey(urlKey);
          setIsAuthenticated(true);
          setAuthError('');
          // Clean URL - preserve the path, only remove the key parameter
          console.log('✅ Key validated, cleaning URL to:', originalPath);
          window.history.replaceState({}, document.title, originalPath);
        } else {
          setApiKey(null);
          setIsAuthenticated(false);
          localStorage.removeItem('salesai-api-key');
          setAuthError('Invalid Access Key provided in URL');
        }
        setIsCheckingAuth(false);
        return;
      }

      // Validate stored key if it exists
      const currentStoredKey = localStorage.getItem('salesai-api-key');
      if (currentStoredKey) {
        const isValid = await testApiKey(currentStoredKey);
        if (isValid) {
          setApiKey(currentStoredKey);
          setIsAuthenticated(true);
          setAuthError('');
        } else {
          setApiKey(null);
          setIsAuthenticated(false);
          localStorage.removeItem('salesai-api-key');
          setAuthError('Stored Access Key is no longer valid');
        }
      }
      
      setIsCheckingAuth(false);
    };

    validateAuth();
  }, []); // Only run once on mount

  // Listen for authentication errors from API interceptor
  useEffect(() => {
    const handleAuthError = (event: CustomEvent) => {
      setAuthError(event.detail.message);
      setApiKey(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth-error', handleAuthError as EventListener);
    
    return () => {
      window.removeEventListener('auth-error', handleAuthError as EventListener);
    };
  }, []);

  // Test Access Key by making a request
  const testApiKey = async (key: string): Promise<boolean> => {
    try {
      // Use the same API URL logic as the main API service
      const getApiBaseUrl = () => {
        if (process.env.REACT_APP_API_URL) {
          return process.env.REACT_APP_API_URL;
        }
        return 'http://localhost:8001';
      };
      
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await fetch(`${API_BASE_URL}/api/v1/deals/stages-scoring`, {
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json',
        },
      });
      
      return response.ok;
    } catch (error) {
      console.error('Auth test failed:', error);
      return false;
    }
  };

  const handleAuthenticated = async (newApiKey: string) => {
    console.log('🔑 Testing API key:', newApiKey.substring(0, 10) + '...');
    setIsValidating(true);
    setAuthError(''); // Clear any previous errors
    const isValid = await testApiKey(newApiKey);
    if (isValid) {
      localStorage.setItem('salesai-api-key', newApiKey);
      setApiKey(newApiKey);
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setIsAuthenticated(false);
      setAuthError('Invalid Access Key. Please check and try again.');
    }
    setIsValidating(false);
  };

  // Show loading while checking auth or validating
  if (isCheckingAuth || isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 dark:border-gray-300 border-t-transparent"></div>
      </div>
    );
  }

  // Show auth page if not authenticated
  if (!apiKey || !isAuthenticated) {
    return <AuthPage onAuthenticated={handleAuthenticated} error={authError} />;
  }

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      {/* Sidebar */}
      <Sidebar 
        className="flex-shrink-0" 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleSidebarToggle}
      />
      
      {/* Main Content Area */}
      <div 
        className="flex-1 min-h-0 flex flex-col transition-all duration-300"
        style={{
          marginRight: isChatOpen ? (isChatCollapsed ? '48px' : '480px') : '0px'
        }}
      >
        <Layout isChatOpen={isChatOpen} isChatCollapsed={isChatCollapsed}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/trends" element={<Trends />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/open-cycles" element={<OpenCyclesTrends />} />
          </Routes>
        </Layout>
      </div>

      {/* ChatWindow positioned at root level to avoid stacking context issues */}
      {isChatOpen && (
        <ChatWindow 
          isOpen={isChatOpen} 
          onToggle={toggleChat}
          onCollapse={handleChatCollapse}
          isCollapsed={isChatCollapsed}
          currentPage={
            window.location.pathname === '/trends' 
              ? 'Performance Trends' 
              : window.location.pathname === '/insights'
              ? 'Sales Insights'
              : 'Sales Pipeline'
          }
          pageContext={
            window.location.pathname === '/trends'
              ? 'Performance trends and analytics over time'
              : window.location.pathname === '/insights'
              ? 'Win rate optimization recommendations and actionable insights'
              : 'Comprehensive view of sales pipeline stages with detailed scoring analysis'
          }
        />
      )}
    </div>
  );
}

export default App; 